import { assertNever } from "assert-never";
import * as monaco from "monaco-editor-core";

import { CodeDiagnosticSeverity } from "../../gen/CodeDiagnostics";
import { SrcSpan } from "../../gen/SrcLoc";
import {
  CellSize,
  createSingleLineHighlight,
  OUTLINE_SHAPE_PADDING,
  outlineShape
} from "./CodeDiagnosticHighlights";
import { convexHullLines } from "./CodeDiagnosticHighlights/ConvexHullLines";
import { LineElement } from "./CodeDiagnosticHighlights/LineElements";

export class CodeDiagnosticHighlightsDecorator {
  constructor(private editor: monaco.editor.ICodeEditor) {}

  clear(): void {
    for (const contentWidgets of this.currentContentWidgets) {
      this.editor.removeContentWidget(contentWidgets);
    }

    this.currentContentWidgets = [];
  }

  setCodeDiagnosticSpans(codeDiagnosticSpans: CodeDiagnosticSpan[]): void {
    this.clear();

    for (const codeDiagnosticSpan of codeDiagnosticSpans) {
      const contentWidget = makeCodeDiagnosticSpanContentWidget(
        this.editor,
        this.nextId(),
        codeDiagnosticSpan
      );
      this.editor.addContentWidget(contentWidget);
      this.currentContentWidgets.push(contentWidget);
    }
  }

  private currentContentWidgets: monaco.editor.IContentWidget[] = [];

  private idCounter: number = 0;

  private nextId(): number {
    return ++this.idCounter;
  }
}

export interface CodeDiagnosticSpan {
  severity: CodeDiagnosticSeverity;
  srcSpan: SrcSpan;
}

function makeCodeDiagnosticSpanContentWidget(
  editor: monaco.editor.ICodeEditor,
  id: number,
  codeDiagnosticSpan: CodeDiagnosticSpan
): monaco.editor.IContentWidget {
  switch (codeDiagnosticSpan.srcSpan.kind) {
    case "lineColRange": {
      const v = codeDiagnosticSpan.srcSpan.value;
      if (v.end.line === v.start.line) {
        const pos: monaco.IPosition = {
          lineNumber: v.start.line,
          column: v.start.col
        };
        const numCols = Math.max(v.end.col - v.start.col, 1);
        return new SingleLineContentWidget(editor, id, codeDiagnosticSpan.severity, pos, numCols);
      } else {
        const pos: monaco.IPosition = {
          lineNumber: v.start.line,
          column: 1
        };
        const lines: LineElement[] = [];
        const model = editor.getModel();
        if (model !== null) {
          lines.push({
            startCol: v.start.col - 1,
            width: model.getLineLastNonWhitespaceColumn(v.start.line) - v.start.col
          });
          for (let i = v.start.line + 1; i < v.end.line; ++i) {
            const start = model.getLineFirstNonWhitespaceColumn(i);
            const end = model.getLineLastNonWhitespaceColumn(i);
            lines.push({
              startCol: start - 1,
              width: end - start
            });
          }
          const start = model.getLineFirstNonWhitespaceColumn(v.end.line);
          lines.push({
            startCol: start - 1,
            width: v.end.col - start
          });
        }
        return new MultiLineContentWidget(
          editor,
          id,
          codeDiagnosticSpan.severity,
          pos,
          convexHullLines(lines)
        );
      }
    }
    case "line": {
      const v = codeDiagnosticSpan.srcSpan.value;
      const pos: monaco.IPosition = {
        lineNumber: v,
        column: 1
      };
      const numCols = 1;
      return new SingleLineContentWidget(editor, id, codeDiagnosticSpan.severity, pos, numCols);
    }
    case "lineCol": {
      const v = codeDiagnosticSpan.srcSpan.value;
      const pos: monaco.IPosition = {
        lineNumber: v.line,
        column: v.col
      };
      const numCols = 1;
      return new SingleLineContentWidget(editor, id, codeDiagnosticSpan.severity, pos, numCols);
    }
    case "lineRange": {
      const v = codeDiagnosticSpan.srcSpan.value;
      const pos: monaco.IPosition = {
        lineNumber: v.startLine,
        column: 1
      };
      const numCols = 1;
      return new SingleLineContentWidget(editor, id, codeDiagnosticSpan.severity, pos, numCols);
    }
    default:
      return assertNever(codeDiagnosticSpan.srcSpan);
  }
}

/**
 * Some string that should be globally unique
 */
const UNIQUE_ID_STR = "pxhzpfvupwsorwnnlemw";

abstract class ContentWidgetBase {
  constructor(private id: number) {}

  allowEditorOverflow = false;
  suppressMouseDown = false;

  getId(): string {
    return `${UNIQUE_ID_STR}-${this.id}`;
  }
}

class SingleLineContentWidget extends ContentWidgetBase implements monaco.editor.IContentWidget {
  constructor(
    private editor: monaco.editor.ICodeEditor,
    id: number,
    private severity: CodeDiagnosticSeverity,
    private position: monaco.IPosition,
    private numCols: number
  ) {
    super(id);
  }

  getDomNode(): HTMLElement {
    const domElem = createSingleLineHighlight(this.numCols);
    this.editor.applyFontInfo(domElem);
    return domElem;
  }

  getPosition(): monaco.editor.IContentWidgetPosition | null {
    return {
      position: this.position,
      preference: [monaco.editor.ContentWidgetPositionPreference.EXACT]
    };
  }
}

class MultiLineContentWidget extends ContentWidgetBase implements monaco.editor.IContentWidget {
  constructor(
    private editor: monaco.editor.ICodeEditor,
    id: number,
    private severity: CodeDiagnosticSeverity,
    private position: monaco.IPosition,
    private lines: LineElement[]
  ) {
    super(id);
  }

  protected domElem: HTMLElement | null = null;

  getDomNode(): HTMLElement {
    const domElem = document.createElement("div");
    domElem.style.pointerEvents = "none";
    domElem.appendChild(document.createTextNode("\xa0"));
    console.log(JSON.stringify(domElem.getBoundingClientRect()));
    this.editor.applyFontInfo(domElem);

    this.domElem = domElem;
    return domElem;
  }

  getPosition(): monaco.editor.IContentWidgetPosition | null {
    return {
      position: this.position,
      preference: [monaco.editor.ContentWidgetPositionPreference.EXACT]
    };
  }

  private initialRendered: boolean = false;

  afterRender(): void {
    if (this.domElem !== null) {
      if (!this.initialRendered) {
        this.initialRendered = true;
        const rect = this.domElem.getBoundingClientRect();

        this.domElem.removeChild(this.domElem.childNodes[0]);

        const cellSize: CellSize = {
          widthPx: rect.width,
          heightPx: rect.height
        };

        this.domElem.appendChild(
          outlineShape(cellSize, this.lines, OUTLINE_SHAPE_PADDING, OUTLINE_SHAPE_PADDING)
        );

        this.domElem.style.marginLeft = `${-OUTLINE_SHAPE_PADDING}px`;
        this.domElem.style.marginTop = `${-OUTLINE_SHAPE_PADDING}px`;
      }
    }
  }
}
